import { getSyncHeaders } from '../../connection/utils';

interface SoundResponse {
	data: {
		id: string;
		status: 'in_progress' | 'completed';
		audio?: string;
		waveform?: string;
		versions?: Array<{
			id: string;
			title: string;
			audio: string;
			duration: number;
			loopable: boolean;
			waveform?: string;
		}>;
	};
}

type ProgressCallback = (data: SoundResponse['data']) => void;
type CompletionCallback = (data: SoundResponse['data']) => void;

class SoundVersionManager {
	private static instance: SoundVersionManager;
	private pendingRequests: Map<string, {
		promise: Promise<SoundResponse['data']>;
		progressCallbacks: ProgressCallback[];
		completionCallbacks: CompletionCallback[];
	}> = new Map();

	private constructor() {
	}

	public static getInstance(): SoundVersionManager {
		if (!SoundVersionManager.instance) {
			SoundVersionManager.instance = new SoundVersionManager();
		}
		return SoundVersionManager.instance;
	}

	private getRequestKey(token: string, trackId: string, duration: number): string {
		return `${token}-${trackId}-${duration}`;
	}

	private notifyProgressCallbacks(data: SoundResponse['data'], callbacks: ProgressCallback[]): void {
		callbacks.forEach(callback => {
			try {
				callback(data);
			} catch (error) {
				console.error('SoundManager: Error in progress callback:', error);
			}
		});
	}

	private notifyCompletionCallbacks(data: SoundResponse['data'], callbacks: CompletionCallback[]): void {
		callbacks.forEach(callback => {
			try {
				callback(data);
			} catch (error) {
				console.error('SoundManager: Error in completion callback:', error);
			}
		});
	}

	public async fetchVersions(
		token: string, 
		trackId: string, 
		durationSeconds: number, 
		options?: {
			onProgress?: ProgressCallback;
			onComplete?: CompletionCallback;
		}
	): Promise<SoundResponse['data']> {
		if (!token) {
			throw new Error('Token not set.');
		}

		const requestKey = this.getRequestKey(token, trackId, durationSeconds);
		
		// Check if there's already a pending request for these parameters
		const existingRequest = this.pendingRequests.get(requestKey);
		if (existingRequest) {
			// Add callbacks to existing request
			if (options?.onProgress) {
				existingRequest.progressCallbacks.push(options.onProgress);
			}
			if (options?.onComplete) {
				existingRequest.completionCallbacks.push(options.onComplete);
			}
			return existingRequest.promise;
		}

		let url = `${process.env.REACT_APP_SYNC_HOST}/v1/epidemic/${trackId}?versionDurationSeconds=${durationSeconds}`;
		
		// Create new request
		const progressCallbacks = options?.onProgress ? [options.onProgress] : [];
		const completionCallbacks = options?.onComplete ? [options.onComplete] : [];
		const promise = this.executeRequest(token, url, requestKey, progressCallbacks, completionCallbacks);
		
		this.pendingRequests.set(requestKey, { promise, progressCallbacks, completionCallbacks });
		
		return promise;
	}

	private async executeRequest(
		token: string, 
		url: string, 
		requestKey: string, 
		progressCallbacks: ProgressCallback[], 
		completionCallbacks: CompletionCallback[]
	): Promise<SoundResponse['data']> {
		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: getSyncHeaders(token),
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const result: SoundResponse = await response.json();
			
			this.notifyProgressCallbacks(result.data, progressCallbacks);
			
			if (result.data?.status === 'completed' && result.data.versions?.length) {
				this.notifyCompletionCallbacks(result.data, completionCallbacks);
				this.pendingRequests.delete(requestKey);
				return result.data;
			}
		} catch (error) {
			this.pendingRequests.delete(requestKey);
			console.error('SoundManager: Error during request:', error);
		}
			
		// Wait 2 seconds before next poll
		await new Promise(resolve => setTimeout(resolve, 2000));
		return this.executeRequest(token, url, requestKey, progressCallbacks, completionCallbacks);
	}

}

export default SoundVersionManager;